import React, { createContext, useContext, useEffect, useState } from "react";
import { authState, api } from "../api/client";
import { CLERK_ENABLED } from "../lib/clerk";
import { togliPass } from "../lib/passPingup";

// Create the auth context
const AuthContext = createContext(null);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    tenants: authState.tenants,
    isLoading: true,
  });

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authState.subscribe((newState) => {
      setAuth((prevAuth) => ({
        ...prevAuth,
        isAuthenticated: newState.isAuthenticated,
        user: newState.user,
        tenants: newState.tenants,
        isLoading: false,
      }));
    });

    // Initial auth check
    const checkAuth = async () => {
      try {
        await authState.refreshSession();
      } catch (error) {
        console.error("Initial auth check failed:", error);
        authState.setAuthenticated(false, null, []);
      } finally {
        setAuth((prevAuth) => ({ ...prevAuth, isLoading: false }));
      }
    };

    checkAuth();

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const contextValue = {
    ...auth,
    login: (returnUrl) => {
      authState.redirectToLogin(returnUrl);
    },
    logout: async () => {
      // Chi esce dall'account esce anche dall'organizzazione del cliente.
      togliPass();
      // Binario Clerk (flag-gated): chiude la sessione Clerk (il redirect lo
      // fa afterSignOutUrl="/" del provider). Il logout legacy resta come
      // pulizia best-effort dell'eventuale cookie "session" residuo.
      if (CLERK_ENABLED && window.Clerk) {
        try {
          await api.auth.logout();
        } catch {
          // il cookie legacy può non esserci: irrilevante
        }
        authState.setAuthenticated(false, null, []);
        await window.Clerk.signOut();
        return;
      }

      try {
        await api.auth.logout();
        // Redirect to login page after logout
        window.location.href = "/login";
      } catch (error) {
        console.error("Logout failed:", error);
        // Force redirect even if API call fails
        authState.setAuthenticated(false, null, []);
        window.location.href = "/login";
      }
    },
    refresh: () => authState.refreshSession(),
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// HOC for components that require authentication
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return <div>Not authenticated</div>;
    }

    return <Component {...props} />;
  };
};
